import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { sortFacilities } from '../../../../src/web/utils/distanceUtils';
import PayOnlineEntry from '../../../components/stateful/PayOnlineEntry';
import { OPTION_DEFAULT, OPTION_LINKOUT } from './properties';
import { SITELINK } from '../../../../constants/softwareProviders';
import withAlert from '../../../hocs/withAlert';
import PayOnlineEntryHeader from '../../../components/stateful/PayOnlineEntryHeader';
import PayOnlineEntryAccordion from '../../../components/stateful/PayOnlineEntryAccordion';
import styles from './PayOnline.scss';
import PayOnlineEntryFilter from '../../../components/stateful/PayOnlineEntryFilter';

const propTypes = {
  alert: PropTypes.node,
  facilities: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    page: PropTypes.shape({
      live: PropTypes.bool,
    }),
    settings: PropTypes.shape({
      softwareProvider: PropTypes.string.isRequired,
    }).isRequired,
  })).isRequired,
  payOption: PropTypes.string,
};

const defaultProps = {
  alert: undefined,
  payOption: OPTION_DEFAULT,
};

const PayOnline = ({ alert, facilities, payOption }) => {
  const [sortedFacilities, setSortedFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(undefined);
  const [panelCloseStates, setPanelCloseStates] = useState({});
  const [stateFullNames, setStateFullNames] = useState([]);
  const [allPanelsExpanded, setAllPanelsExpanded] = useState(true);
  const [allPanelsCollapsed, setAllPanelsCollapsed] = useState(false);

  useEffect(() => {
    const sortedLiveFacilities = sortFacilities(facilities).filter(
      facility => facility.page && facility.page.live,
    );
    setSortedFacilities(sortedLiveFacilities);
    setStateFullNames([...new Set(
      sortedLiveFacilities.map(facility => facility.address.stateFullName),
    )].sort());
  }, [facilities]);


  const groupFacilitiesByState = (facilitiesToBeGrouped) => {
    const stateFacilitiesMap = {};
    facilitiesToBeGrouped.forEach((facility) => {
      const { state, country, stateFullName } = facility.address;
      const stateKey = `${state}-${country}`;
      if (!stateFacilitiesMap[stateKey]) {
        stateFacilitiesMap[stateKey] = {};
        stateFacilitiesMap[stateKey].facilities = [facility];
        stateFacilitiesMap[stateKey].stateFullName = stateFullName;
      } else {
        stateFacilitiesMap[stateKey].facilities.push(facility);
      }
    });

    const sortedStates = Object.keys(stateFacilitiesMap).sort((keyA, keyB) => {
      const stateFullNameA = stateFacilitiesMap[keyA].stateFullName || '';
      const stateFullNameB = stateFacilitiesMap[keyB].stateFullName || '';
      return stateFullNameA.localeCompare(stateFullNameB);
    });

    return { stateFacilitiesMap, sortedStates };
  };


  const expandAllPanels = () => {
    setPanelCloseStates({});
    setAllPanelsExpanded(true);
    setAllPanelsCollapsed(false);
  };

  const collapseAllPanels = (sortedStates) => {
    setPanelCloseStates(() => {
      const updatedPanelCloseStates = {};
      sortedStates.forEach((state) => {
        updatedPanelCloseStates[state] = true;
      });
      return updatedPanelCloseStates;
    });
    setAllPanelsCollapsed(true);
    setAllPanelsExpanded(false);
  };

  const togglePanel = (state) => {
    setPanelCloseStates((prevState) => {
      const updatedPanelCloseStates = {
        ...prevState,
        [state]: !prevState[state],
      };

      const allPanelsClosedStatesValues = Object.values(updatedPanelCloseStates);
      const numberOfStatePanels = stateFullNames.length;
      const numberOfPanelsClosed = allPanelsClosedStatesValues
        .filter(panelState => panelState === true);
      const allPanelsClosed = numberOfPanelsClosed.length === numberOfStatePanels;

      const allPanelsOpen = allPanelsClosedStatesValues
        .every(panelState => panelState === false);

      if (!filteredFacilities) {
        setAllPanelsCollapsed(allPanelsClosed);
        setAllPanelsExpanded(allPanelsOpen);
      } else {
        const { sortedStates } = groupFacilitiesByState(filteredFacilities);
        let numberOfClosedPanels = 0;
        sortedStates.forEach((stateKey) => {
          if (updatedPanelCloseStates[stateKey]) {
            numberOfClosedPanels += 1;
          }
        });

        setAllPanelsCollapsed(numberOfClosedPanels === sortedStates.length);
        setAllPanelsExpanded(numberOfClosedPanels === 0);
      }

      return updatedPanelCloseStates;
    });
  };


  const handleSearchAndFilter = (facilitiesFiltered) => {
    setFilteredFacilities(facilitiesFiltered);
    if (facilitiesFiltered) {
      expandAllPanels();
    }
  };

  const toggleIframe = (foundFacility) => {
    setShowIframe(prevShowIframe => !prevShowIframe);
    setSelectedFacility(foundFacility);
  };

  const renderPayOnlineEntry = (facility) => {
    const { settings: { softwareProvider } } = facility;
    const entryPayOption = softwareProvider === SITELINK ? OPTION_LINKOUT : payOption;

    return (
      <PayOnlineEntry
        facility={facility}
        toggleIframe={toggleIframe}
        showingIframe={showIframe}
        payOption={entryPayOption}
      />
    );
  };

  const renderFacilitiesEntry = () => {
    if (!facilities || facilities.length === 0) {
      return null;
    }

    const { sortedStates,
      stateFacilitiesMap } = groupFacilitiesByState(filteredFacilities || sortedFacilities);

    if (showIframe) {
      const foundFacility = sortedFacilities.find(
        facility => facility.id === selectedFacility.id,
      );
      return (
        <div>
        <PayOnlineEntryFilter
          expandAllPanels={expandAllPanels}
          collapseAllPanels={collapseAllPanels}
          sortedFacilities={sortedFacilities}
          filteredFacilities={filteredFacilities}
          sortedStates={sortedStates}
          stateFullNames={stateFullNames}
          onSearchAndFilter={handleSearchAndFilter}
          allPanelsExpanded
          allPanelsCollapsed
        />
        <PayOnlineEntryHeader showSort={false} />
        {renderPayOnlineEntry(foundFacility)}
        </div>
      );
    }

    const noFacilitiesFound = sortedStates.length === 0;

    return (
      <>
      <PayOnlineEntryFilter
        expandAllPanels={expandAllPanels}
        collapseAllPanels={collapseAllPanels}
        sortedFacilities={sortedFacilities}
        filteredFacilities={filteredFacilities}
        sortedStates={sortedStates}
        stateFullNames={stateFullNames}
        onSearchAndFilter={handleSearchAndFilter}
        allPanelsCollapsed={noFacilitiesFound ? true : allPanelsCollapsed}
        allPanelsExpanded={noFacilitiesFound ? true : allPanelsExpanded}
      />
      {(noFacilitiesFound) ? (
        <div className={styles.noResultsFound}>
          No Results Found
        </div>
      ) : (
       <PayOnlineEntryAccordion
         renderPayOnlineEntry={facility => renderPayOnlineEntry(facility)}
         togglePanel={togglePanel}
         stateFacilitiesMap={stateFacilitiesMap}
         sortedStates={sortedStates}
         panelCloseStates={panelCloseStates}
       />
      )}
      </>
    );
  };

  const renderIframe = () => {
    if (showIframe) {
      return (
        <iframe
          title="pay online"
          src={selectedFacility.tenantPortalUrl}
          height="600px"
          width="100%"
        />
      );
    }
    return '';
  };

  return (
    <>
        {alert && alert}
        {renderFacilitiesEntry()}
        <div className={styles.iframe}>
          {renderIframe()}
        </div>
    </>
  );
};

PayOnline.propTypes = propTypes;
PayOnline.defaultProps = defaultProps;

PayOnline.fragments = {
  facility: gql`
    fragment PayOnlineFacility on Facility {
      id
      __typename
      settings {
        softwareProvider
      }
      ...PayOnlineEntryFacility
    }

    ${PayOnlineEntry.fragments.facility}
  `,
  page: gql`
    fragment PayOnlinePage on Page {
      id
      __typename
      live
    }
  `,
};

export default withAlert({
  Component: PayOnline,
  message: 'If you are trying to pay your bill online or access your account, please try again later or contact the facility.',
});
